// src/components/FeaturedInfo/FeaturedInfo.jsx
import { useEffect, useState } from "react";

const CEUTI_LOGO = "/img/escudo.png";

const isCeuti = (teamName = "") => {
  const n = teamName.toLowerCase();
  return (
    n.includes("unión áfrica") ||
    n.includes("union africa") ||
    n.includes("ceutí") ||
    n.includes("ceuti")
  );
};

const COMPETICION_BADGE_CLASSES =
  "inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700";

const getTeamLogo = (teamName) => {
  if (!teamName) return null;
  const n = teamName.toLowerCase();

  if (
    n.includes("unión áfrica") ||
    n.includes("union africa") ||
    n.includes("ceutí") ||
    n.includes("ceuti")
  ) {
    return CEUTI_LOGO;
  }

  if (n.includes("melistar")) return "/img/Melistar.png";
  if (n.includes("pinatar")) return "/img/Pinatar.png";

  return null;
};

const getStandingsLogo = (name, remoteLogo) =>
  isCeuti(name) ? CEUTI_LOGO : remoteLogo || getTeamLogo(name);

/**
 * Fetch robusto: lee texto y hace JSON.parse manual con reintentos.
 * Evita el error "Unexpected token '<'" cuando el backend devuelve HTML.
 */
const fetchJsonWithRetry = async (
  url,
  { tries = 2, backoffMs = 250, fetchOptions = {} } = {}
) => {
  let lastErr = null;

  for (let i = 0; i < tries; i++) {
    try {
      const res = await fetch(url, {
        cache: "no-store",
        ...fetchOptions,
      });

      const text = await res.text();

      if (!res.ok) {
        // Devuelve un error con info útil, sin romper JSON.parse
        throw new Error(
          `HTTP ${res.status} en ${url}: ${text.slice(0, 120)}`.trim()
        );
      }

      // Intentamos parsear
      try {
        return JSON.parse(text);
      } catch (e) {
        // Si viene HTML, suele empezar por "<"
        const head = text.trim().slice(0, 80);
        throw new Error(
          `Respuesta no JSON en ${url}: ${head || "(vacía)"}`
        );
      }
    } catch (err) {
      lastErr = err;
      // Backoff antes de reintentar
      if (i < tries - 1) {
        await new Promise((r) => setTimeout(r, backoffMs * (i + 1)));
        continue;
      }
    }
  }

  throw lastErr || new Error(`Fallo al cargar ${url}`);
};

const FeaturedInfo = () => {
  const [standings, setStandings] = useState([]);
  const [nextMatch, setNextMatch] = useState(null);
  const [lastMatch, setLastMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      try {
        setLoading(true);
        setError(null);

        const [standingsData, matchesData] = await Promise.all([
          fetchJsonWithRetry("/api/clasificacion.php", { tries: 2 }),
          fetchJsonWithRetry("/api/proximo-partido.php", { tries: 3 }),
        ]);

        if (cancelled) return;

        // --- CLASIFICACIÓN ---
        if (!Array.isArray(standingsData)) {
          throw new Error("Respuesta de clasificación no válida");
        }

        const grouped = new Map();

        for (const rawTeam of standingsData) {
          const name = (rawTeam.name ?? rawTeam.equipo ?? "").trim();
          if (!name) continue;

          const played = Number(rawTeam.played ?? rawTeam.pj ?? 0) || 0;
          const wins = Number(rawTeam.wins ?? rawTeam.pg ?? 0) || 0;
          const draws = Number(rawTeam.draws ?? rawTeam.pe ?? 0) || 0;
          const losses = Number(rawTeam.losses ?? rawTeam.pp ?? 0) || 0;
          const goalsFor = Number(rawTeam.goalsFor ?? rawTeam.gf ?? 0) || 0;
          const goalsAgainst =
            Number(rawTeam.goalsAgainst ?? rawTeam.gc ?? 0) || 0;
          const points = Number(rawTeam.points ?? rawTeam.puntos ?? 0) || 0;

          const logo = rawTeam.logo ?? null;
          const racha = Array.isArray(rawTeam.racha) ? rawTeam.racha : [];

          if (!grouped.has(name)) {
            grouped.set(name, {
              name,
              played: 0,
              wins: 0,
              draws: 0,
              losses: 0,
              goalsFor: 0,
              goalsAgainst: 0,
              points: 0,
              logo: logo ?? null,
              racha: racha,
            });
          }

          const team = grouped.get(name);
          team.played += played;
          team.wins += wins;
          team.draws += draws;
          team.losses += losses;
          team.goalsFor += goalsFor;
          team.goalsAgainst += goalsAgainst;
          team.points += points;

          if (!team.logo && logo) team.logo = logo;
          if ((!team.racha || team.racha.length === 0) && racha.length)
            team.racha = racha;
        }

        const combined = Array.from(grouped.values());

        combined.sort((a, b) => {
          if (b.points !== a.points) return b.points - a.points;
          const gdA = a.goalsFor - a.goalsAgainst;
          const gdB = b.goalsFor - b.goalsAgainst;
          if (gdB !== gdA) return gdB - gdA;
          if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor;
          return a.name.localeCompare(b.name, "es");
        });

        const adaptedStandings = combined.map((team, index) => ({
          id: index + 1,
          position: index + 1,
          name: team.name,
          played: team.played,
          wins: team.wins,
          draws: team.draws,
          losses: team.losses,
          goalsFor: team.goalsFor,
          goalsAgainst: team.goalsAgainst,
          points: team.points,
          logo: team.logo ?? null,
          racha: Array.isArray(team.racha) ? team.racha : [],
        }));

        setStandings(adaptedStandings);

        // --- PARTIDOS (PRÓXIMO / ÚLTIMO) ---
        if (matchesData && !matchesData.error) {
          setNextMatch(matchesData.nextMatch ?? null);
          setLastMatch(matchesData.lastMatch ?? null);
        } else {
          console.warn("Respuesta de proximo-partido.php no válida:", matchesData);
          // No lo tratamos como error “fatal”: simplemente no mostramos partido
          setNextMatch(null);
          setLastMatch(null);
        }
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        setError(err?.message || "Error al cargar datos");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
    };
  }, []);

  const renderMatchTeams = (match) => {
    if (!match) return null;

    const homeLogo = isCeuti(match.local)
      ? CEUTI_LOGO
      : match.escudoLocal || getTeamLogo(match.local);

    const awayLogo = isCeuti(match.visitante)
      ? CEUTI_LOGO
      : match.escudoVisitante || getTeamLogo(match.visitante);

    return (
      <div className="flex flex-col items-center gap-2">
        <div className="flex items-center justify-center gap-4">
          <div className="flex items-center gap-2">
            {homeLogo && (
              <img
                src={homeLogo}
                alt={match.local}
                className="w-8 h-8 rounded-full object-contain bg-slate-100"
                loading="lazy"
                decoding="async"
              />
            )}
            <p className="font-semibold text-slate-900">{match.local}</p>
          </div>

          <span className="px-3 py-1 rounded-full bg-slate-100 text-xs font-semibold text-slate-700">
            VS
          </span>

          <div className="flex items-center gap-2">
            <p className="font-semibold text-slate-900">{match.visitante}</p>
            {awayLogo && (
              <img
                src={awayLogo}
                alt={match.visitante}
                className="w-8 h-8 rounded-full object-contain bg-slate-100"
                loading="lazy"
                decoding="async"
              />
            )}
          </div>
        </div>

        <div className="flex justify-between w-full text-xs text-slate-500 mt-1">
          <span>Local</span>
          <span>Visitante</span>
        </div>
      </div>
    );
  };

  const renderRacha = (racha) => {
    const arr = Array.isArray(racha) ? racha.slice(0, 5) : [];
    if (arr.length !== 5) return null;

    return (
      <div className="flex gap-1 justify-center">
        {arr.map((r, i) => (
          <span
            key={i}
            className="px-1.5 py-0.5 rounded bg-slate-100 text-[11px] font-semibold text-slate-600"
            title="Últimos 5"
          >
            {r}
          </span>
        ))}
      </div>
    );
  };

  return (
    <section className="bg-transparent">
      <div className="max-w-6xl mx-auto px-4 py-6 md:py-6">
        <div className="grid gap-6 md:grid-cols-2">
          {/* COLUMNA 1: Próximo partido + Último resultado */}
          <div className="flex flex-col gap-6">
            {/* Próximo partido */}
            <article className="bg-white/90 rounded-2xl shadow-md border border-slate-100 p-6 flex flex-col gap-4">
              <header className="flex items-center justify-between gap-2">
                <h2 className="text-lg font-semibold text-slate-900">
                  Próximo partido
                </h2>
                {nextMatch?.competicion && (
                  <span className={COMPETICION_BADGE_CLASSES}>
                    {nextMatch.competicion}
                  </span>
                )}
              </header>

              {loading ? (
                <p className="text-sm text-slate-500">Cargando datos…</p>
              ) : error ? (
                <p className="text-sm text-red-600">{error}</p>
              ) : nextMatch ? (
                <>
                  <p className="text-sm text-slate-500">
                    Jornada{" "}
                    <span className="font-semibold">
                      {nextMatch.jornada ?? "-"}
                    </span>
                  </p>

                  {renderMatchTeams(nextMatch)}

                  <div className="flex flex-col gap-1 text-sm text-slate-600 mt-2">
                    <p>
                      <span className="font-medium">Fecha:</span>{" "}
                      {nextMatch.fecha || "Por confirmar"}
                      {nextMatch.hora && ` · ${nextMatch.hora}`}
                    </p>
                  </div>
                </>
              ) : (
                <p className="text-sm text-slate-500">
                  No se ha encontrado información del próximo partido.
                </p>
              )}
            </article>

            {/* Último resultado */}
            <article className="bg-white/90 rounded-2xl shadow-md border border-slate-100 p-6 flex flex-col gap-4">
              <header className="flex items-center justify-between gap-2">
                <h2 className="text-lg font-semibold text-slate-900">
                  Último resultado
                </h2>
                {lastMatch?.competicion && (
                  <span className={COMPETICION_BADGE_CLASSES}>
                    {lastMatch.competicion}
                  </span>
                )}
              </header>

              {loading ? (
                <p className="text-sm text-slate-500">Cargando datos…</p>
              ) : error ? (
                <p className="text-sm text-red-600">{error}</p>
              ) : lastMatch ? (
                <>
                  <p className="text-sm text-slate-500">
                    Jornada{" "}
                    <span className="font-semibold">
                      {lastMatch.jornada ?? "-"}
                    </span>
                  </p>

                  {renderMatchTeams(lastMatch)}

                  <div className="flex items-center justify-center my-2">
                    <div className="inline-flex items-baseline gap-2 rounded-full bg-slate-100 px-4 py-2">
                      <span className="text-lg font-bold text-slate-900">
                        {lastMatch.golesLocal ?? "-"}
                      </span>
                      <span className="text-sm font-semibold text-slate-500">
                        -
                      </span>
                      <span className="text-lg font-bold text-slate-900">
                        {lastMatch.golesVisitante ?? "-"}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1 text-sm text-slate-600">
                    <p>
                      <span className="font-medium">Fecha:</span>{" "}
                      {lastMatch.fecha || "No disponible"}
                    </p>
                  </div>
                </>
              ) : (
                <p className="text-sm text-slate-500">
                  No se ha encontrado información del último partido.
                </p>
              )}
            </article>
          </div>

          {/* COLUMNA 2: Clasificación */}
          <article className="bg-white/90 rounded-2xl shadow-md border border-slate-100 p-6">
            <header className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">
                Clasificación
              </h2>
              <span className="text-xs text-slate-500">Temporada 25-26</span>
            </header>

            {loading && !standings.length ? (
              <p className="text-sm text-slate-500">Cargando clasificación…</p>
            ) : error ? (
              <p className="text-sm text-red-600">{error}</p>
            ) : (
              <>
                {/* Móvil: tarjetas */}
                <div className="md:hidden space-y-2">
                  {standings.map((team) => {
                    const ceuti = isCeuti(team.name);
                    const logo = getStandingsLogo(team.name, team.logo);

                    return (
                      <div
                        key={team.id}
                        className={`border rounded-lg px-3 py-2 text-xs ${
                          ceuti
                            ? "bg-indigo-50/80 font-semibold border-indigo-200"
                            : "bg-white border-slate-200"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">#{team.position}</span>

                          <span className="flex-1 mx-2 text-slate-800 text-[11px] truncate flex items-center gap-2">
                            {logo && (
                              <img
                                src={logo}
                                alt={team.name}
                                className="w-5 h-5 rounded-full object-contain bg-slate-100"
                                loading="lazy"
                                decoding="async"
                              />
                            )}
                            {team.name}
                          </span>

                          <span className="text-slate-900 font-bold">
                            {team.points} pts
                          </span>
                        </div>

                        <div className="mt-1 flex flex-wrap justify-between gap-x-2 gap-y-1 text-[10px] text-slate-500">
                          <span>PJ {team.played}</span>
                          <span>G {team.wins}</span>
                          <span>E {team.draws}</span>
                          <span>P {team.losses}</span>
                          <span>GF {team.goalsFor}</span>
                          <span>GC {team.goalsAgainst}</span>
                        </div>

                        <div className="mt-2">{renderRacha(team.racha)}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Desktop/Tablet: tabla */}
                <div className="hidden md:block relative max-h-80 overflow-hidden rounded-xl border border-slate-100 mt-2 md:mt-0">
                  <div className="max-h-80 overflow-y-auto">
                    <table className="min-w-full text-xs md:text-sm">
                      <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr className="text-left text-slate-500">
                          <th className="px-3 py-2">#</th>
                          <th className="px-3 py-2">Equipo</th>
                          <th className="px-3 py-2 text-center">Racha</th>
                          <th className="px-2 py-2 text-center">PJ</th>
                          <th className="px-2 py-2 text-center">G</th>
                          <th className="px-2 py-2 text-center">E</th>
                          <th className="px-2 py-2 text-center">P</th>
                          <th className="px-2 py-2 text-center">GF</th>
                          <th className="px-2 py-2 text-center">GC</th>
                          <th className="px-2 py-2 text-center">Pts</th>
                        </tr>
                      </thead>
                      <tbody>
                        {standings.map((team) => {
                          const ceuti = isCeuti(team.name);
                          const logo = getStandingsLogo(team.name, team.logo);

                          return (
                            <tr
                              key={team.id}
                              className={`border-t border-slate-100 ${
                                ceuti ? "bg-indigo-50/80 font-semibold" : "bg-white"
                              }`}
                            >
                              <td className="px-3 py-2 text-slate-700">
                                {team.position}
                              </td>

                              <td className="px-3 py-2 text-slate-800">
                                <div className="flex items-center gap-2">
                                  {logo && (
                                    <img
                                      src={logo}
                                      alt={team.name}
                                      className="w-6 h-6 rounded-full object-contain bg-slate-100"
                                      loading="lazy"
                                      decoding="async"
                                    />
                                  )}
                                  <span>{team.name}</span>
                                </div>
                              </td>

                              <td className="px-3 py-2">{renderRacha(team.racha)}</td>

                              <td className="px-2 py-2 text-center">{team.played}</td>
                              <td className="px-2 py-2 text-center">{team.wins}</td>
                              <td className="px-2 py-2 text-center">{team.draws}</td>
                              <td className="px-2 py-2 text-center">{team.losses}</td>
                              <td className="px-2 py-2 text-center">{team.goalsFor}</td>
                              <td className="px-2 py-2 text-center">{team.goalsAgainst}</td>
                              <td className="px-2 py-2 text-center text-slate-900">
                                {team.points}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <p className="mt-3 text-[11px] text-slate-400">
                  Datos obtenidos de BeSoccer.
                </p>
              </>
            )}
          </article>
        </div>
      </div>
    </section>
  );
};

export default FeaturedInfo;
